import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from 'components/Header/Navbar';
import Button from 'components/Button';
import { listingService } from 'api/services/ListingService';
import { topCategoryService } from 'api/services/TopCategoryService';
import { conditionService } from 'api/services/ConditionService';
import { warrantyService } from 'api/services/WarrantyService';
import { secondaryCategoryService } from 'api/services/SecondaryCategoryService';
import { productTypeService } from 'api/services/ProductTypeService';
import { cityService } from 'api/services/CityService';
import { districtService } from 'api/services/DistrictService';
import type { TopCategoryViewModel } from 'api/types/top-category';
import type { CityViewModel } from 'api/types/city';
import type { DistrictViewModel } from 'api/types/district';
import type { ConditionViewModel } from 'api/types/condition';
import type { WarrantyViewModel } from 'api/types/warranty';
import type { SecondaryCategoryViewModel } from 'api/types/secondary-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import {
  SpecConstraintViewModel,
  SpecValueType,
} from 'api/types/product-type/SpecConstraintViewModel';

interface FilterState {
  mainCategory: string;
  brand: string;
  cpuSeries: string;
  cpuModel: string;
  gpu: string;
  ram: string;
  ssd: string;
  hdd: string;
  os: string;
  screenSize: string;
  resolution: string;
  priceRange: string;
  condition: string;
  location: string;
  sortBy: string;
  minPrice?: string;
  maxPrice?: string;
  cpuGeneration?: string;
  ramType?: string;
  peripheralType?: string;
  desktopPartType?: string;
  desktopCategory?: string;
  desktopBrand?: string;
  bulkSaleType?: string;
  laptopCategory?: string;
  laptopBrand?: string;
  laptopHardwareType?: string;
  laptopPartType?: string;
  serverCategory?: string;
  monitorCategory?: string;
  equipmentCategory?: string;
  // Anakart filtreleri
  motherboardBrand?: string;
  motherboardSocket?: string;
  motherboardChipset?: string;
  motherboardFormFactor?: string;
  // RAM filtreleri
  ramBrand?: string;
  ramCapacity?: string;
  ramSpeed?: string;
  // Ekran kartı filtreleri
  gpuBrand?: string; 
  gpuMemory?: string;
  gpuModel?: string;
  // HDD filtreleri
  hddBrand?: string; 
  hddCapacity?: string;
  hddRpm?: string;
  // SSD filtreleri
  ssdBrand?: string; 
  ssdCapacity?: string;
  ssdInterface?: string;
  // Güç kaynağı filtreleri
  psuBrand?: string;
  psuWattage?: string;
  psuCertification?: string;
  // Optik sürücü filtreleri
  opticalType?: string;
  // Fan filtreleri
  fanSize?: string;
  fanRgb?: string;
  fanType?: string;
  // İnternet kartı filtreleri
  networkType?: string;
  // Monitör filtreleri
  monitorBrand?: string;
  monitorSize?: string;
  monitorResolution?: string;
  monitorRefreshRate?: string;
  // Ekipman filtreleri
  equipmentCompatibleModel?: string;
  equipmentSize?: string;
  // Sunucu filtreleri
  serverHardwareType?: string;
  connectionType?: string;
  keyboardType?: string;
  usbCapacity?: string;
  usbHubType?: string;
  headsetType?: string;
  speakerType?: string;
  modemType?: string;
  province?: string;
  district?: string;
}

interface FormData {
  category: string;
  secondaryCategoryId: string;
  productTypeId: string;
  brand: string;
  cpuModel: string;
  ram: string;
  storage: string;
  gpu: string;
  price: string;
  title: string;
  description: string;
  condition: string;
  warranty: string;
  testedSystem: boolean;
  city: string;
  district: string;
  neighborhood: string;
  termsAccepted: boolean;
  images: FileList | null;
}

/** A single selected spec row: constraint (or null when choosing) and user value. */
interface SelectedSpecRow {
  specConstraintId: number | null;
  value: string;
}

/** Map category name to slug for form conditionals (desktop, laptop, etc.). */
function topCategorySlug(tc: TopCategoryViewModel): string {
  const n = (tc.name ?? '').toLowerCase();
  if (n.includes('masaüstü') || n.includes('masaustu')) return 'desktop';
  if (n.includes('dizüstü') || n.includes('laptop')) return 'laptop';
  if (n.includes('çevre') || n.includes('peripheral')) return 'peripherals';
  if (n.includes('monitör') || n.includes('monitor')) return 'monitor';
  if (n.includes('konsol') || n.includes('console')) return 'console';
  if (n.includes('ekipman') || n.includes('equipment')) return 'equipment';
  if (n.includes('sunucu') || n.includes('server')) return 'server';
  return String(tc.id);
}

const Ilan_ver: React.FC = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [conditions, setConditions] = useState<ConditionViewModel[]>([]);
  const [warranties, setWarranties] = useState<WarrantyViewModel[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategoryViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [specConstraints, setSpecConstraints] = useState<SpecConstraintViewModel[]>([]);
  const [selectedSpecs, setSelectedSpecs] = useState<SelectedSpecRow[]>([]);
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [userForm, setFormData] = useState<FormData>({
    category: '',
    secondaryCategoryId: '',
    productTypeId: '',
    brand: '',
    cpuModel: '',
    ram: '',
    storage: '',
    gpu: '',
    price: '',
    title: '',
    description: '',
    condition: '',
    warranty: '',
    testedSystem: false,
    city: '',
    district: '',
    neighborhood: '',
    termsAccepted: false,
    images: null
  });

  // Filtreler için state
  const [filters, setFilters] = useState<FilterState>({
    mainCategory: 'all',
    brand: 'all',
    cpuSeries: '',
    cpuModel: '',
    gpu: '',
    ram: '',
    ssd: '',
    hdd: '',
    os: '',
    screenSize: '',
    resolution: '',
    priceRange: '',
    condition: '',
    location: '',
    sortBy: 'newest',
    minPrice: '',
    maxPrice: '',
    cpuGeneration: '',
    ramType: '',
    peripheralType: '',
    desktopPartType: '',
    desktopCategory: '',
    desktopBrand: '',
    bulkSaleType: '',
    laptopCategory: '',
    laptopBrand: '',
    laptopHardwareType: '',
    laptopPartType: '',
    serverCategory: '',
    monitorCategory: '',
    equipmentCategory: '',
    // Anakart filtreleri
    motherboardBrand: '',
    motherboardSocket: '',
    motherboardChipset: '',
    motherboardFormFactor: '',
    // RAM filtreleri
    ramBrand: '',
    ramCapacity: '',
    ramSpeed: '',
    // Ekran kartı filtreleri
    gpuBrand: '', 
    gpuMemory: '',
    gpuModel: '',
    // HDD filtreleri
    hddBrand: '', 
    hddCapacity: '',
    hddRpm: '',
    // SSD filtreleri
    ssdBrand: '', 
    ssdCapacity: '',
    ssdInterface: '',
    // Güç kaynağı filtreleri
    psuBrand: '',
    psuWattage: '',
    psuCertification: '',
    // Optik sürücü filtreleri
    opticalType: '',
    // Fan filtreleri
    fanSize: '',
    fanRgb: '',
    fanType: '',
    // İnternet kartı filtreleri
    networkType: '',
    // Monitör filtreleri
    monitorBrand: '',
    monitorSize: '',
    monitorResolution: '',
    monitorRefreshRate: '',
    // Ekipman filtreleri
    equipmentCompatibleModel: '',
    equipmentSize: '',
    // Sunucu filtreleri
    serverHardwareType: '',
    connectionType: '',
    keyboardType: '',
    usbCapacity: '',
    usbHubType: '',
    headsetType: '',
    speakerType: '',
    modemType: '',
    province: '',
    district: '',
  });

  const handleChange = (e: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        images: e.target.files
      }));
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [tcRes, condRes, warrRes] = await Promise.all([
          topCategoryService.getAll(),
          conditionService.getAll(),
          warrantyService.getAll(),
        ]);
        if (tcRes.isSuccess && tcRes.data) setTopCategories(tcRes.data);
        if (condRes.isSuccess && condRes.data) setConditions(condRes.data);
        if (warrRes.isSuccess && warrRes.data) setWarranties(warrRes.data);
      } catch {
        // Fallback: keep empty; dropdowns will show no options or static fallback
      }
    };
    load();
  }, []);

  useEffect(() => {
    cityService.getAll().then((res) => {
      if (res.isSuccess && res.data) setCities(res.data);
    });
  }, []);

  useEffect(() => {
    const cityId = userForm.city ? Number(userForm.city) : NaN;
    if (!userForm.city || Number.isNaN(cityId)) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((res) => {
      if (res.isSuccess && res.data) setDistricts(res.data);
    });
  }, [userForm.city]);

  const handleTopCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    const tc = topCategories.find(c => String(c.id) === id);
    setFormData(prev => ({ ...prev, category: id, secondaryCategoryId: '', productTypeId: '' }));
    setSecondaryCategories([]);
    setProductTypes([]);
    if (tc) {
      setFilters(prev => ({ ...prev, mainCategory: topCategorySlug(tc) }));
      const topCategoryIdNum = Number(id);
      if (!Number.isNaN(topCategoryIdNum)) {
        secondaryCategoryService.getAll({ topCategoryId: topCategoryIdNum }).then((res) => {
          if (res.isSuccess && res.data) setSecondaryCategories(res.data);
        });
      }
    } else {
      setFilters(prev => ({ ...prev, mainCategory: id || 'all' }));
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setFormData(prev => ({ ...prev, city: id, district: '' }));
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, district: e.target.value }));
  };

  const handleSecondaryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setFormData(prev => ({ ...prev, secondaryCategoryId: id, productTypeId: '' }));
    setProductTypes([]);
    const secondaryIdNum = Number(id);
    if (id && !Number.isNaN(secondaryIdNum)) {
      productTypeService.getAll({ secondaryCategoryId: secondaryIdNum }).then((res) => {
        if (res.isSuccess && res.data) setProductTypes(res.data);
      });
    }
  };

  const handleProductTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setFormData(prev => ({ ...prev, productTypeId: id }));
    setSpecConstraints([]);
    setSelectedSpecs([]);
    if (id && !Number.isNaN(id)) {
      const productTypeIdNum = Number(id);
      productTypeService.getSpecConstraints(productTypeIdNum).then((res) => {
        if (res.isSuccess && res.data) setSpecConstraints(res.data);
      });
    }
  };

  /** IDs of constraints already chosen in selectedSpecs (for dropdown filtering). */
  const usedSpecConstraintIds = selectedSpecs
    .map((r) => r.specConstraintId)
    .filter((id): id is number => id != null);

  const availableSpecConstraints = specConstraints.filter(
    (sc) => !usedSpecConstraintIds.includes(sc.id)
  );

  const canAddSpec =
    specConstraints.length > 0 && selectedSpecs.length < specConstraints.length;

  const addSpecRow = () => {
    setSelectedSpecs((prev) => [...prev, { specConstraintId: null, value: '' }]);
  };

  const updateSpecConstraint = (rowIndex: number, specConstraintId: number) => {
    setSelectedSpecs((prev) =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, specConstraintId, value: '' } : row
      )
    );
  };

  const updateSpecValue = (rowIndex: number, value: string) => {
    setSelectedSpecs((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, value } : row))
    );
  };

  const removeSpecRow = (rowIndex: number) => {
    setSelectedSpecs((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!userForm.title || !userForm.price) {
      alert('Lütfen gerekli alanları doldurun');
      return;
    }

    if (!userForm.termsAccepted) {
      alert('Lütfen kullanım koşullarını kabul edin');
      return;
    }

    const productTypeId = userForm.productTypeId;
    if (productTypes.length > 0 && !productTypeId) {
      alert('Lütfen ürün tipini seçin.');
      return;
    }

    const city = filters.province || userForm.city;
    const district = filters.district || userForm.district;
    const brand = userForm.brand;
    const cpuModel = userForm.cpuModel;
    const specs: Record<string, string> = {
      ...(brand && { Marka: brand }),
      ...(cpuModel && { 'İşlemci Modeli': cpuModel }),
      ...(userForm.ram && { RAM: userForm.ram }),
      ...(userForm.storage && { Depolama: userForm.storage }),
      ...(userForm.gpu && { 'Ekran Kartı': userForm.gpu }),
    };

    const specsJson: Record<string, string> = {};
    selectedSpecs.forEach((row) => {
      if (row.specConstraintId == null || !row.value.trim()) return;
      const constraint = specConstraints.find((sc) => sc.id === row.specConstraintId);
      if (constraint) specsJson[constraint.key] = row.value.trim();
    });

    setSubmitting(true);
    try {
      const applicationForm = new FormData();
      applicationForm.append('Title', userForm.title);
      applicationForm.append('Description', userForm.description ?? '');
      applicationForm.append('Price', String(userForm.price));
      applicationForm.append('ConditionId', userForm.condition || '');
      applicationForm.append('ProductTypeId', productTypeId || '');
      applicationForm.append('CityId', city || '');
      applicationForm.append('DistrictId', district || '');
      const hasWarrantyFromApi = warranties.find((w) => String(w.id) === userForm.warranty)?.value === true;
      applicationForm.append('HasWarranty', (hasWarrantyFromApi ?? userForm.warranty === 'yes') ? 'true' : 'false');
      applicationForm.append('IsTested', userForm.testedSystem ? 'true' : 'false');
      if (Object.keys(specs).length > 0) {
        applicationForm.append('Specs', JSON.stringify(specs));
      }
      if (Object.keys(specsJson).length > 0) {
        applicationForm.append('SpecsJson', JSON.stringify(specsJson));
      }
      if (userForm.images?.length) {
        for (let i = 0; i < userForm.images.length; i++) {
          const file = userForm.images[i];
          if (file && file.type.startsWith('image/')) {
            applicationForm.append('Images', file);
          }
        }
      }

      const result = await listingService.create(applicationForm);
      if (result.isSuccess) {
        alert('İlan başarıyla oluşturuldu!');
        navigate('/');
      } else {
        alert(result.message ?? 'İlan oluşturulamadı.');
      }
    } catch (err) {
      console.error('İlan oluşturma hatası:', err);
      alert(err instanceof Error ? err.message : 'İlan oluşturulurken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h2 className="card-title text-center mb-4">İlan Ver</h2>
              <form onSubmit={handleSubmit} className="needs-validation">
                {/* Filtreler Kartı */}
                <div className="card mb-4">
                  <div className="card-body">
                    <h5 className="card-title mb-3">İlan Özellikleri</h5>
                    
                    {/* Tüm Kategoriler - API yanıtına göre doldurulur */}
                    <div className="mb-3">
                      <label className="form-label">Tüm Kategoriler</label>
                      <select
                        className="form-select"
                        name="category"
                        value={userForm.category}
                        onChange={handleTopCategoryChange}
                        required
                      >
                        <option value="">Seçiniz</option>
                        {topCategories.map((tc) => (
                          <option key={tc.id} value={tc.id}>
                            {tc.name ?? `Kategori ${tc.id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Alt Kategori - İkincil kategori API yanıtına göre doldurulur */}
                    {secondaryCategories.length > 0 && (
                      <div className="mb-3">
                        <label className="form-label">Alt Kategori</label>
                        <select
                          className="form-select"
                          name="secondaryCategoryId"
                          value={userForm.secondaryCategoryId}
                          onChange={handleSecondaryCategoryChange}
                        >
                          <option value="">Seçiniz</option>
                          {secondaryCategories.map((sc) => (
                            <option key={sc.id} value={sc.id}>
                              {sc.name ?? `Kategori ${sc.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Parça Türü - Ürün tipi API yanıtına göre doldurulur */}
                    {productTypes.length > 0 && (
                      <div className="mb-3">
                        <label className="form-label">Ürün Tipi</label>
                        <select
                          className="form-select"
                          name="productTypeId"
                          value={userForm.productTypeId}
                          onChange={handleProductTypeChange}
                          required
                        >
                          <option value="">Seçiniz</option>
                          {productTypes.map((pt) => (
                            <option key={pt.id} value={pt.id}>
                              {pt.name ?? `Ürün tipi ${pt.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {specConstraints.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">Ürün Özellikleri</label>
                          {canAddSpec && selectedSpecs.length === 0 && (
                            <Button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={addSpecRow}
                            >
                              Özellik Ekle
                            </Button>
                          )}
                        </div>
                        {selectedSpecs.map((row, rowIndex) => {
                          const constraint =
                            row.specConstraintId != null
                              ? specConstraints.find((sc) => sc.id === row.specConstraintId)
                              : null;
                          const valueType = constraint?.valueType ?? SpecValueType.String;
                          const isEnum = valueType === SpecValueType.Enum;
                          const allowedValues = constraint?.allowedValues ?? [];

                          const controlHeightStyle = { minHeight: '38px' };

                          return (
                            <div key={rowIndex} className="d-flex align-items-stretch gap-2 mb-2">
                              {row.specConstraintId == null ? (
                                <select
                                  className="form-select flex-grow-1"
                                  style={controlHeightStyle}
                                  value=""
                                  onChange={(e) => {
                                    const id = e.target.value ? Number(e.target.value) : 0;
                                    if (id) updateSpecConstraint(rowIndex, id);
                                  }}
                                  aria-label="Özellik seçin"
                                >
                                  <option value="">Özellik seçin</option>
                                  {availableSpecConstraints.map((sc) => (
                                    <option key={sc.id} value={sc.id}>
                                      {sc.displayName}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <>
                                  <select
                                    className="form-select flex-grow-1"
                                    style={{ ...controlHeightStyle, flex: '1 1 50%', maxWidth: '50%' }}
                                    value={row.specConstraintId}
                                    disabled
                                    aria-label={constraint?.displayName}
                                  >
                                    <option value={row.specConstraintId}>
                                      {constraint?.displayName}
                                    </option>
                                  </select>
                                  {isEnum ? (
                                    <select
                                      className="form-select flex-grow-1"
                                      style={{ ...controlHeightStyle, flex: '1 1 50%', maxWidth: '50%' }}
                                      value={row.value}
                                      onChange={(e) =>
                                        updateSpecValue(rowIndex, e.target.value)
                                      }
                                      aria-label={`${constraint?.displayName} değeri`}
                                    >
                                      <option value="">Seçiniz</option>
                                      {allowedValues.map((av) => (
                                        <option key={av.id} value={av.value}>
                                          {av.value}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={valueType === SpecValueType.Number ? 'number' : 'text'}
                                      className="form-control flex-grow-1"
                                      style={{ ...controlHeightStyle, flex: '1 1 50%', maxWidth: '50%' }}
                                      value={row.value}
                                      onChange={(e) =>
                                        updateSpecValue(rowIndex, e.target.value)
                                      }
                                      placeholder={constraint?.displayName}
                                      aria-label={`${constraint?.displayName} değeri`}
                                    />
                                  )}
                                </>
                              )}
                              <Button
                                type="button"
                                className="btn btn-outline-danger btn-sm align-self-center flex-shrink-0"
                                onClick={() => removeSpecRow(rowIndex)}
                                aria-label="Özelliği kaldır"
                              >
                                ×
                              </Button>
                            </div>
                          );
                        })}
                        {canAddSpec && selectedSpecs.length > 0 && (
                          <div className="mt-2">
                            <Button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={addSpecRow}
                            >
                              Özellik Ekle
                            </Button>
                          </div>
                        )}
                      </div>
                      )}

                    {/* Ürün Durumu - API yanıtına göre doldurulur */}
                    <div className="mb-3 mt-4">
                      <h6 className="mb-3">Ürün Durumu</h6>
                      <select
                        className="form-select"
                        name="condition"
                        value={userForm.condition}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz</option>
                        {conditions.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name ?? `Durum ${c.id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Garanti Durumu - API yanıtına göre doldurulur */}
                    <div className="mb-3 mt-4">
                      <h6 className="mb-3">Garanti Durumu</h6>
                      <select
                        className="form-select"
                        name="warranty"
                        value={userForm.warranty}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seçiniz</option>
                        {warranties.map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name ?? `Garanti ${w.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Başlık */}
                <div className="form-group mb-3">
                      <label className="form-label">İlan Başlığı</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={userForm.title}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Açıklama */}
                    <div className="form-group mb-3">
                      <label className="form-label">Açıklama</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={userForm.description}
                        onChange={handleChange}
                        rows={4}
                        required
                      />
                    </div>

                    {/* Adres Bilgileri */}
                    <div className="card mb-4 border-0 shadow-sm">
                      <div className="card-body p-4">
                        <h5 className="card-title mb-3 fw-bold">Adres Bilgileri</h5>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">İl</label>
                            <select
                              className="form-select"
                              name="city"
                              value={userForm.city}
                              onChange={handleCityChange}
                              required
                              aria-label="İl seçin"
                            >
                              <option value="">Seçiniz</option>
                              {cities.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name ?? `İl ${c.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">İlçe</label>
                            <select
                              className="form-select"
                              name="district"
                              value={userForm.district}
                              onChange={handleDistrictChange}
                              required
                              disabled={!userForm.city}
                              aria-label="İlçe seçin"
                            >
                              <option value="">Seçiniz</option>
                              {districts.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name ?? `İlçe ${d.id}`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Birleştirilmiş Fotoğraf/Video ve Test Edilen Sistem Bölümü */}
                    <div className="card mb-4 border-0 shadow-sm">
                  <div className="card-body p-4">
                    {/* Fotoğraf ve Video Ekle */}
                    <div className="form-group mb-4">
                      <h5 className="card-title mb-3 fw-bold">Fotoğraf ve Video Ekle</h5>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleImageChange}
                        required
                      />
                      <small className="text-muted">En az 3, en fazla 10 fotoğraf ekleyebilirsiniz. Video eklemeniz tavsiye edilir.</small>
                    </div>

                    <hr className="my-4" />

                    {/* Test Edilen Sistem Rozeti */}
                    <h5 className="card-title mb-3 fw-bold">Test Edilen Sistem Rozeti Almak İstiyor musunuz?</h5>
                    <p className="card-text mb-3">
                      Test edilen sistem rozeti, bilgisayarınızın temel bileşenlerinin (işlemci, RAM, disk, ekran kartı) 
                      test edildiğini ve kararlı çalıştığını gösteren bir işarettir. Bu sayede ilanınız daha fazla güven uyandırır.
                    </p>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="testedSystem"
                        name="testedSystem"
                        checked={userForm.testedSystem}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form-check-label" htmlFor="testedSystem">
                        Evet, sistemimi test etmek ve rozet almak istiyorum
                      </label>
                    </div>
                    {/* Test Detayları Akordiyon */}
                    <div className="accordion mt-3" id="testDetailsAccordion">
                      <div className="accordion-item border-0 bg-light rounded mb-2">
                        <h2 className="accordion-header" id="headingOne">
                          <Button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="false" aria-controls="collapseOne">
                            <strong>1. İşlemci Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseOne" className="accordion-collapse collapse" aria-labelledby="headingOne" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              İşlemcinin kararlılığını ve sıcaklığının görmek için CoreTemp ve Cpu-z programı kullanılır:
                            </p>
                            <ul>
                              <li>CoreTemp: İşlemci sıcaklığını gösterir</li>
                              <li>Cpu-z: İşlemcinin detaylı özelliklerini gösterir ve stress testi özelliği vardır</li>
                            </ul>
                            <p><strong>Test Adımları:</strong></p>
                            <ol>
                              <li>Cpu-z programında bench kısmı seçilir ve stress cpu butonuna basılır</li>
                              <li>Test bir kaç dakika çalıştırılır</li>
                              <li>Sıcaklık kontrolü yapılır (70-75 derece normal, 80-85 derece üzeri riskli)</li>
                            </ol>
                            <div className="alert alert-warning mt-2">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Not:</strong> İşlemciniz 80-85 dereceyi hızlı bir şekilde geçmeye başlarsa testi durdurunuz. 
                              İşlemci Termal macun değişimi gerekebilir. Bu test işlemcinize zarar vermemekte aksine yüksek sıcaklık 
                              kaynaklı oluşan işlemci ömrü ve performans düşüşü hakkında bize bilgi vermektedir.
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 bg-light rounded mb-2">
                        <h2 className="accordion-header" id="headingTwo">
                          <Button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                            <strong>2. RAM Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              Techpowerup Memtest64 programı ile Windows üzerinde test yapılır:
                            </p>
                            <ol>
                              <li>Tested memory kısmından maximum seçilir</li>
                              <li>Stop after kısmına 2 yazılır</li>
                              <li>Begin test ile test başlatılır</li>
                              <li>Herhangi bir hata tespit edilmezse sağlamdır</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 bg-light rounded mb-2">
                        <h2 className="accordion-header" id="headingThree">
                          <Button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                            <strong>3. HDD/SSD Sağlamlık Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              HDD Sentinel programı ile sistemdeki HDD ve SSD'lerin sağlık ve performans değerleri kontrol edilir.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="accordion-item border-0 bg-light rounded">
                        <h2 className="accordion-header" id="headingFour">
                          <Button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="false" aria-controls="collapseFour">
                            <strong>4. Ekran Kartı Testi</strong>
                          </Button>
                        </h2>
                        <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#testDetailsAccordion">
                          <div className="accordion-body">
                            <p>
                              Geeks3D Furmark programı ile ekran kartı kararlılık testi yapılır:
                            </p>
                            <ol>
                              <li>Graphics test kısmından FurMark(GL) veya FurMark(VK) seçilir</li>
                              <li>Resolution seçeneğinden ekran çözünürlüğü ayarlanır</li>
                              <li>Run butonu ile test başlatılır (3-10 dakika arası) test yapılır daha sonra durdurulur</li>
                              <li>Test sırasında ekran da çizgiler ve kararma olmuyorsa ekran kartı kararlılığı iyi durumda diyebiliriz</li>
                            </ol>
                            <div className="alert alert-warning mt-2">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              <strong>Not:</strong> Ekran kartı sıcaklığı 70-75 derece arası normal kabul edilir, 85 derece üzerine 
                              çıkması durumunda testi durdurunuz. Bilgisayarınızda oyunlarda takılma donma gibi sorunlar oluşuyorsa 
                              ekran kartı termal macun değişimi gerekebilir. Bu test doğru bir şekilde yapıldığında ekran kartına 
                              zarar vermemektedir.
                            </div>
                            <p className="mt-3">
                              En iyi test yöntemi, ekran kartı kullanımı yüksek oyunlarda birkaç saat test etmektir. 
                              Bu sayede sistemin genel kararlılığı hakkında da bilgi sahibi oluruz.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fiyat - Buraya taşıyoruz */}
                <div className="form-group mb-3">
                  <label className="form-label">Fiyat (TL)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={userForm.price}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>

                {/* İlan Sözleşmesi */}
                <div className="form-check mb-4 mt-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="terms"
                    name="termsAccepted"
                    checked={userForm.termsAccepted}
                    onChange={handleCheckboxChange}
                    required
                  />
                  <label className="form-check-label" htmlFor="terms">
                    <a href="#" className="text-decoration-none" data-bs-toggle="modal" data-bs-target="#termsModal">
                      İlan Sözleşmesi</a>'ni okudum ve kabul ediyorum
                  </label>
                </div>

                {/* Gönder Butonu */}
                <div className="text-center">
                  <Button 
                    type="submit" 
                    className="btn btn-primary btn-lg w-100"
                    disabled={!userForm.termsAccepted || submitting}
                    loading={submitting}
                  >
                    İlanı Yayınla
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* İlan Sözleşmesi Modal */}
      <div className="modal fade" id="termsModal" tabIndex={-1} aria-labelledby="termsModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="termsModalLabel">İlan Sözleşmesi</h5>
              <Button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></Button>
            </div>
            <div className="modal-body">
              <h6>SadeceBilgisayar.com İlan Kullanım Koşulları</h6>
              <p>
                Bu sözleşme, SadeceBilgisayar.com platformunda ilan veren kullanıcılar ile site yönetimi arasındaki hak ve yükümlülükleri belirler.
              </p>
              <h6>1. Genel Kurallar</h6>
              <p>
                - Kullanıcılar, platformda verdikleri ilanların içeriğinden tamamen kendileri sorumludur.<br/>
                - Telif hakkı ihlali içeren içerikler paylaşılamaz.<br/>
                - Yasadışı ürünlerin satışına yönelik ilanlar yayınlanamaz.<br/>
                - Her kullanıcı, kendi hesabını ve şifresini korumakla yükümlüdür.
              </p>
              <h6>2. İlan İçerikleri</h6>
              <p>
                - İlan başlıkları ve açıklamaları gerçeği yansıtmalıdır.<br/>
                - Ürün görselleri, satılan ürüne ait olmalıdır.<br/>
                - Fiyat bilgisi doğru ve güncel olmalıdır.
              </p>
              <h6>3. İlan Moderasyonu</h6>
              <p>
                SadeceBilgisayar.com, uygunsuz içerik barındıran ilanları kaldırma, düzenleme veya reddetme hakkını saklı tutar.
              </p>
              <p className="mb-0">
                <Link to="/ilan-sozlesmesi" target="_blank" rel="noopener noreferrer" className="text-primary">
                  Tam metni görüntüle
                </Link>
              </p>
            </div>
            <div className="modal-footer">
              <Button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Kapat</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Ilan_ver;

